import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Checkbox,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  MdClose,
  MdArrowForward,
  MdArrowBack,
  MdSave,
  MdEdit,
  MdDragIndicator,
  MdFormatBold,
  MdQrCode2,
  MdRefresh,
  MdCheckCircle,
  MdVisibility,
  MdAdd,
  MdDelete,
  MdImage,
  MdTextFields,
  MdNumbers,
  MdCloudUpload,
} from "react-icons/md";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "react-toastify";
import { updateCustomerQrTemplate } from "../../utils/api";

const INITIAL_FIELDS = [
  { id: "name", label: "Name", type: "text", printOnLabel: true, includeInQr: true, printPrefix: true, showToCustomer: true, defaultValue: "", x: 4, y: 8, fontSize: 13, fontWeight: "bold" },
  { id: "agency", label: "Agency", type: "text", printOnLabel: true, includeInQr: true, printPrefix: true, showToCustomer: true, defaultValue: "", x: 4, y: 22, fontSize: 11, fontWeight: "normal" },
  { id: "center", label: "Center", type: "text", printOnLabel: true, includeInQr: true, printPrefix: true, showToCustomer: true, defaultValue: "", x: 4, y: 35, fontSize: 11, fontWeight: "normal" },
  { id: "cropYear", label: "Crop year", type: "text", printOnLabel: true, includeInQr: true, printPrefix: true, showToCustomer: true, defaultValue: "2025-2026", x: 4, y: 48, fontSize: 11, fontWeight: "normal" },
  { id: "commodity", label: "Commodity", type: "text", printOnLabel: true, includeInQr: true, printPrefix: true, showToCustomer: true, defaultValue: "Paddy", x: 4, y: 61, fontSize: 11, fontWeight: "normal" },
  { id: "fssai", label: "Fssai LIC no", type: "text", printOnLabel: true, includeInQr: true, printPrefix: true, showToCustomer: true, defaultValue: "", x: 4, y: 74, fontSize: 10, fontWeight: "normal" },
  { id: "srNo", label: "Sr. No", type: "text", printOnLabel: true, includeInQr: true, printPrefix: true, showToCustomer: true, defaultValue: "1", x: 4, y: 87, fontSize: 12, fontWeight: "bold" },
];

const SUGGESTIONS = {
  agency: ["PUNSUP", "PUNGRAIN", "MARKFED", "FCI", "PSWC", "HAFED"],
  cropYear: ["2024-2025", "2025-2026", "2026-2027"],
  commodity: ["Paddy", "Rice", "Wheat", "Paddy PR-126", "Paddy 1509", "Paddy Basmati"],
};

const clamp = (min, max, val) => Math.max(min, Math.min(max, val));

export const computeSequentialSerial = (baseValue, index = 0) => {
  const str = String(baseValue || "1").trim();
  if (!str) return (index + 1).toString();
  const match = str.match(/^(.*?)(\d+)$/);
  if (match) {
    const prefix = match[1];
    const numStr = match[2];
    const startNum = parseInt(numStr, 10);
    const currentNum = startNum + index;
    const padded = String(currentNum).padStart(numStr.length, "0");
    return `${prefix}${padded}`;
  }
  return `${str}${index + 1}`;
};

const QrTemplateModal = ({ open, setOpen, customer, onSaved }) => {
  const [step, setStep] = useState(1);
  const [length, setLength] = useState(100);
  const [height, setHeight] = useState(50);
  const [up, setUp] = useState(1);
  const [fields, setFields] = useState(INITIAL_FIELDS);
  const [saving, setSaving] = useState(false);

  // Freeform QR Code placement
  const [qrX, setQrX] = useState(68);
  const [qrY, setQrY] = useState(14);
  const [qrDimension, setQrDimension] = useState(82);
  const [printQr, setPrintQr] = useState(true);

  // Layout Styles
  const [qrPosition, setQrPosition] = useState("right");
  const [borderStyle, setBorderStyle] = useState("standard");

  // Selection & Freeform Drag state
  const [selectedId, setSelectedId] = useState("qrCode");
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef(null);
  const canvasRef = useRef(null);

  // Custom Field CRUD Modal State
  const [isAddFieldOpen, setIsAddFieldOpen] = useState(false);
  const [newFieldForm, setNewFieldForm] = useState({
    label: "",
    type: "text", // "text" | "number" | "image"
    defaultValue: "",
    printOnLabel: true,
    includeInQr: true,
    printPrefix: true,
    showToCustomer: true,
    imageWidth: 40,
    imageHeight: 40,
  });

  const getCustomerDefault = useCallback((fieldId, cust) => {
    if (!cust) return "";
    if (fieldId === "name") return cust.name || cust.userName || "";
    if (fieldId === "agency") return cust.agency || "";
    if (fieldId === "center") return cust.location || "";
    if (fieldId === "fssai") return cust.fssai || "";
    if (fieldId === "cropYear") return "2025-2026";
    if (fieldId === "commodity") return "Paddy";
    if (fieldId === "srNo") return "1";
    return "";
  }, []);

  const setInitialDefaults = useCallback((cust) => {
    setLength(100);
    setHeight(50);
    setUp(1);
    setQrPosition("right");
    setQrX(68);
    setQrY(14);
    setQrDimension(82);
    setBorderStyle("standard");
    setFields(
      INITIAL_FIELDS.map((f) => ({
        ...f,
        defaultValue: getCustomerDefault(f.id, cust),
      }))
    );
  }, [getCustomerDefault]);

  useEffect(() => {
    if (open && customer) {
      setStep(1);
      const existing = customer.qrTemplate;
      if (existing && existing.length) {
        setLength(existing.length || 100);
        setHeight(existing.height || 50);
        setUp(existing.up || 1);
        setQrPosition(existing.qrPosition || "right");
        setQrX(existing.qrX !== undefined ? existing.qrX : existing.qrPosition === "left" ? 6 : 68);
        setQrY(existing.qrY !== undefined ? existing.qrY : 14);
        setQrDimension(existing.qrDimension || 82);
        setPrintQr(existing.printQr !== undefined ? Boolean(existing.printQr) : true);
        setBorderStyle(existing.borderStyle || "standard");

        if (existing.fields && existing.fields.length > 0) {
          const initialIds = new Set(INITIAL_FIELDS.map((f) => f.id));
          const existingMap = new Map(existing.fields.map((f) => [f.id, f]));
          
          // Merge defaults with saved settings
          const merged = INITIAL_FIELDS.map((df) => {
            const ef = existingMap.get(df.id);
            if (ef) {
              return {
                ...df,
                ...ef,
                x: ef.x !== undefined ? ef.x : df.x,
                y: ef.y !== undefined ? ef.y : df.y,
                fontSize: ef.fontSize || df.fontSize,
                fontWeight: ef.fontWeight || df.fontWeight,
                defaultValue:
                  ef.defaultValue !== undefined && ef.defaultValue !== ""
                    ? ef.defaultValue
                    : getCustomerDefault(df.id, customer),
                printOnLabel: ef.printOnLabel !== undefined ? ef.printOnLabel : true,
                includeInQr: ef.includeInQr !== undefined ? ef.includeInQr : true,
                printPrefix: ef.printPrefix !== undefined ? ef.printPrefix : true,
                showToCustomer: ef.showToCustomer !== undefined ? ef.showToCustomer : true,
                type: ef.type || df.type || "text",
              };
            }
            return {
              ...df,
              defaultValue: getCustomerDefault(df.id, customer),
            };
          });

          // Also preserve all custom fields created by user!
          const customFields = existing.fields
            .filter((f) => !initialIds.has(f.id) || f.isCustom)
            .map((cf) => ({
              ...cf,
              type: cf.type || "text",
              isCustom: true,
              imageWidth: cf.imageWidth || 40,
              imageHeight: cf.imageHeight || 40,
              printOnLabel: cf.printOnLabel !== undefined ? cf.printOnLabel : true,
              includeInQr: cf.includeInQr !== undefined ? cf.includeInQr : (cf.type !== "image"),
              printPrefix: cf.printPrefix !== undefined ? cf.printPrefix : true,
              showToCustomer: cf.showToCustomer !== undefined ? cf.showToCustomer : true,
            }));

          setFields([...merged, ...customFields]);
        } else {
          setInitialDefaults(customer);
        }
      } else {
        setInitialDefaults(customer);
      }
    }
  }, [open, customer?._id]);

  const handleFieldChange = (id, key, value) => {
    setFields((prev) =>
      prev.map((f) => (f.id === id ? { ...f, [key]: value } : f))
    );
  };

  // Helper to read image files as Base64 Data URL
  const handleImageUpload = (file, callback) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload a valid image file (PNG, JPG, SVG, WebP)");
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      toast.error("Image file size should be less than 3MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      callback(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  // Add a new Custom Field
  const handleAddNewField = () => {
    if (!newFieldForm.label || !newFieldForm.label.trim()) {
      toast.error("Please enter a field name / label");
      return;
    }
    const newId = "custom_" + Date.now();
    const isImg = newFieldForm.type === "image";
    const createdField = {
      id: newId,
      label: newFieldForm.label.trim(),
      type: newFieldForm.type || "text",
      isCustom: true,
      printOnLabel: newFieldForm.printOnLabel !== false,
      includeInQr: isImg ? false : (newFieldForm.includeInQr !== false),
      printPrefix: isImg ? false : (newFieldForm.printPrefix !== false),
      showToCustomer: newFieldForm.showToCustomer !== false,
      defaultValue: newFieldForm.defaultValue || "",
      x: 8,
      y: Math.min(80, 10 + (fields.length * 8) % 75),
      fontSize: 11,
      fontWeight: "normal",
      imageWidth: Number(newFieldForm.imageWidth) || 40,
      imageHeight: Number(newFieldForm.imageHeight) || 40,
    };
    setFields((prev) => [...prev, createdField]);
    setSelectedId(newId);
    setIsAddFieldOpen(false);
    setNewFieldForm({
      label: "",
      type: "text",
      defaultValue: "",
      printOnLabel: true,
      includeInQr: true,
      printPrefix: true,
      showToCustomer: true,
      imageWidth: 40,
      imageHeight: 40,
    });
    toast.success(`Custom field "${createdField.label}" added successfully!`);
  };

  // Delete a Field
  const handleDeleteField = (fieldId) => {
    setFields((prev) => prev.filter((f) => f.id !== fieldId));
    if (selectedId === fieldId) {
      setSelectedId("qrCode");
    }
    toast.success("Field deleted");
  };

  // 1-Click Preset Layouts
  const applyPreset = (presetName) => {
    if (presetName === "qr-right") {
      setQrPosition("right");
      setQrX(68);
      setQrY(14);
      setFields((prev) =>
        prev.map((f, i) => ({
          ...f,
          x: 4,
          y: 8 + i * 13,
        }))
      );
    } else if (presetName === "qr-left") {
      setQrPosition("left");
      setQrX(6);
      setQrY(14);
      setFields((prev) =>
        prev.map((f, i) => ({
          ...f,
          x: 36,
          y: 8 + i * 13,
        }))
      );
    } else if (presetName === "two-column") {
      setQrPosition("right");
      setQrX(70);
      setQrY(16);
      setFields((prev) =>
        prev.map((f, i) => ({
          ...f,
          x: i % 2 === 0 ? 4 : 36,
          y: 10 + Math.floor(i / 2) * 20,
        }))
      );
    }
    toast.info(`Layout Preset applied!`);
  };

  // Drag & Drop Handlers for Freeform 2D Canvas
  const handlePointerDown = (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedId(id);
    setIsDragging(true);

    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();

    let startX = 0;
    let startY = 0;
    if (id === "qrCode") {
      startX = qrX;
      startY = qrY;
    } else {
      const f = fields.find((item) => item.id === id);
      if (f) {
        startX = f.x !== undefined ? f.x : 4;
        startY = f.y !== undefined ? f.y : 8;
      }
    }

    dragRef.current = {
      id,
      clientX: e.clientX,
      clientY: e.clientY,
      startX,
      startY,
      rectWidth: rect.width,
      rectHeight: rect.height,
    };

    window.addEventListener("mousemove", handlePointerMove);
    window.addEventListener("mouseup", handlePointerUp);
  };

  const handlePointerMove = (e) => {
    if (!dragRef.current) return;
    const { id, clientX, clientY, startX, startY, rectWidth, rectHeight } =
      dragRef.current;

    const deltaXPx = e.clientX - clientX;
    const deltaYPx = e.clientY - clientY;

    const deltaXPercent = (deltaXPx / rectWidth) * 100;
    const deltaYPercent = (deltaYPx / rectHeight) * 100;

    const newX = Math.round(clamp(0, 92, startX + deltaXPercent));
    const newY = Math.round(clamp(0, 92, startY + deltaYPercent));

    if (id === "qrCode") {
      setQrX(newX);
      setQrY(newY);
    } else {
      setFields((prev) =>
        prev.map((f) => (f.id === id ? { ...f, x: newX, y: newY } : f))
      );
    }
  };

  const handlePointerUp = () => {
    setIsDragging(false);
    dragRef.current = null;
    window.removeEventListener("mousemove", handlePointerMove);
    window.removeEventListener("mouseup", handlePointerUp);
  };

  const nudgeSelected = (dx, dy) => {
    if (selectedId === "qrCode") {
      setQrX((prev) => clamp(0, 95, prev + dx));
      setQrY((prev) => clamp(0, 95, prev + dy));
    } else {
      setFields((prev) =>
        prev.map((f) =>
          f.id === selectedId
            ? {
                ...f,
                x: clamp(0, 95, (f.x || 0) + dx),
                y: clamp(0, 95, (f.y || 0) + dy),
              }
            : f
        )
      );
    }
  };

  // Save Fixed Layout Template
  const handleSave = async () => {
    const customerId = customer?._id;
    if (!customerId) {
      toast.error("Customer ID not found");
      return;
    }

    setSaving(true);
    const payload = {
      length: Number(length),
      height: Number(height),
      up: Number(up),
      qrPosition,
      qrX,
      qrY,
      qrDimension,
      printQr: printQr !== false,
      borderStyle,
      fields: fields.map((f) => ({
        id: f.id,
        label: f.label,
        type: f.type || "text",
        isCustom: !!f.isCustom,
        printOnLabel: f.printOnLabel !== false,
        includeInQr: f.includeInQr !== false,
        printPrefix: f.printPrefix !== false,
        showToCustomer: f.showToCustomer !== false,
        defaultValue: f.defaultValue || "",
        x: f.x !== undefined ? f.x : 4,
        y: f.y !== undefined ? f.y : 8,
        fontSize: f.fontSize || 11,
        fontWeight: f.fontWeight || "normal",
        imageWidth: f.imageWidth || 40,
        imageHeight: f.imageHeight || 40,
      })),
    };

    const [success, data] = await updateCustomerQrTemplate(customerId, payload);
    if (success) {
      toast.success("✅ Fixed QR & Label Template saved successfully!");
      onSaved?.();
      setOpen(false);
    } else {
      toast.error(data?.message || "Failed to save QR template");
    }
    setSaving(false);
  };

  // Active printable fields
  const activePrintFields = fields.filter((f) => f.printOnLabel !== false);

  // Generate QR string
  const getPreviewQrData = (sampleSr = "1") => {
    const lines = [];
    fields.forEach((f) => {
      if (f.includeInQr !== false) {
        if (f.type === "image") {
          if (f.defaultValue && !f.defaultValue.startsWith("data:")) {
            lines.push(`${f.label}: ${f.defaultValue}`);
          }
        } else {
          const val = f.id === "srNo" ? sampleSr : (f.defaultValue || `Sample ${f.label}`);
          if (f.id === "srNo" || f.printPrefix === false) {
            lines.push(val);
          } else {
            lines.push(`${f.label}: ${val}`);
          }
        }
      }
    });
    return lines.join("\n");
  };

  const getComputedBorderStyle = (bStyle) => {
    if (bStyle === "none") return "border-0";
    if (bStyle === "thin") return "border border-black";
    if (bStyle === "dotted") return "border-2 border-dotted border-black";
    return "border-2 border-black";
  };

  // Canvas visual scaling in Step 4
  const canvasWidthPx = Math.min(560, Math.max(380, Number(length) * 4.8));
  const canvasHeightPx = Math.round((canvasWidthPx * Number(height)) / Number(length));

  // High-def preview scale for Step 5
  const previewSheetWidthPx = Math.min(420, Math.max(300, Number(length) * 3.8));
  const previewSheetHeightPx = Math.round((previewSheetWidthPx * Number(height)) / Number(length));
  const previewScale = previewSheetWidthPx / 480;

  const selectedField = fields.find((f) => f.id === selectedId);

  const getTypeIcon = (type) => {
    if (type === "image") return "🖼️";
    if (type === "number") return "🔢";
    return "🔤";
  };

  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg" fullWidth>
      {/* Suggestions Datalists */}
      <datalist id="agency-options">
        {SUGGESTIONS.agency.map((a) => (
          <option key={a} value={a} />
        ))}
      </datalist>
      <datalist id="cropYear-options">
        {SUGGESTIONS.cropYear.map((c) => (
          <option key={c} value={c} />
        ))}
      </datalist>
      <datalist id="commodity-options">
        {SUGGESTIONS.commodity.map((c) => (
          <option key={c} value={c} />
        ))}
      </datalist>

      {/* HEADER */}
      <DialogTitle className="bg-slate-900 text-white flex items-center justify-between p-4">
        <div className="flex items-center gap-2">
          <span className="text-xl">🏷️</span>
          <div>
            <h2 className="text-base md:text-lg font-black tracking-tight">
              QR Code Setup — {customer?.name || customer?.userName || "Customer"}
            </h2>
            <p className="text-[11px] text-emerald-400 font-semibold">
              QR Print Text Studio & Builder & Multi-Up Sheet Designer
            </p>
          </div>
        </div>
        <IconButton onClick={() => setOpen(false)} size="small" className="text-white hover:bg-slate-800">
          <MdClose />
        </IconButton>
      </DialogTitle>

      {/* 5-STEP BREADCRUMB PROGRESS BAR */}
      <div className="bg-gray-50 border-b border-gray-200 px-6 py-2.5 flex items-center justify-between flex-wrap gap-2 text-xs select-none">
        {[
          { num: 1, label: "Page Size & Up" },
          { num: 2, label: "Select Print Fields", icon: "🖨️" },
          { num: 3, label: "Field Settings & Values", icon: "⚙️" },
          { num: 4, label: "QR Print Text Builder", icon: "🎨" },
          { num: 5, label: "Final Print Preview", icon: "👁️" },
        ].map((s) => (
          <button
            key={s.num}
            type="button"
            onClick={() => setStep(s.num)}
            className={`flex items-center gap-1.5 pb-1 border-b-2 transition cursor-pointer ${
              step === s.num
                ? "border-emerald-600 text-emerald-800 font-bold"
                : "border-transparent text-gray-500 hover:text-gray-800"
            }`}
          >
            <span
              className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold ${
                step === s.num
                  ? "bg-emerald-600 text-white"
                  : step > s.num
                  ? "bg-emerald-100 text-emerald-800"
                  : "bg-gray-200 text-gray-600"
              }`}
            >
              {step > s.num ? "✓" : s.num}
            </span>
            <span>{s.icon ? `${s.icon} ` : ""}{s.label}</span>
          </button>
        ))}
      </div>

      <DialogContent className="p-6 bg-white min-h-[440px] flex flex-col justify-between">
        {/* Step 1: Page Size, Height & Up */}
        {step === 1 && (
          <div className="flex flex-col gap-6 py-2">
            <div className="bg-emerald-50/60 border border-emerald-200 rounded-xl p-4 text-emerald-950 text-xs md:text-sm">
              <strong className="font-bold">Step 1: Physical Label Dimensions & Up Count</strong>
              <p className="mt-1 text-emerald-900 text-xs">
                Aapke printer ke sticker roll ka physical size kya hai? Ek row me kitne labels hain (Up count)?
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="flex flex-col gap-1.5 bg-slate-50 p-4 rounded-xl border border-slate-200">
                <label className="text-xs font-bold text-gray-700 flex items-center justify-between">
                  <span>Label Length (Width)</span>
                  <span className="text-emerald-700 font-bold font-mono">{length} mm</span>
                </label>
                <TextField
                  type="number"
                  value={length}
                  onChange={(e) => setLength(Number(e.target.value))}
                  inputProps={{ min: 20, max: 300 }}
                  size="small"
                  variant="outlined"
                  className="bg-white"
                />
                <span className="text-[11px] text-gray-500">Standard single label width in mm (e.g. 100mm)</span>
              </div>

              <div className="flex flex-col gap-1.5 bg-slate-50 p-4 rounded-xl border border-slate-200">
                <label className="text-xs font-bold text-gray-700 flex items-center justify-between">
                  <span>Label Height</span>
                  <span className="text-emerald-700 font-bold font-mono">{height} mm</span>
                </label>
                <TextField
                  type="number"
                  value={height}
                  onChange={(e) => setHeight(Number(e.target.value))}
                  inputProps={{ min: 10, max: 300 }}
                  size="small"
                  variant="outlined"
                  className="bg-white"
                />
                <span className="text-[11px] text-gray-500">Standard single label height in mm (e.g. 50mm)</span>
              </div>

              <div className="flex flex-col gap-1.5 bg-slate-50 p-4 rounded-xl border border-slate-200">
                <label className="text-xs font-bold text-gray-700 flex items-center justify-between">
                  <span>Up Count (Labels per row)</span>
                  <span className="text-emerald-700 font-bold font-mono">{up} Up</span>
                </label>
                <TextField
                  type="number"
                  value={up}
                  onChange={(e) => setUp(Math.max(1, Number(e.target.value)))}
                  inputProps={{ min: 1, max: 6 }}
                  size="small"
                  variant="outlined"
                  className="bg-white"
                />
                <span className="text-[11px] text-gray-500">1 = Single, 2 = 2-Up side-by-side, 3 = 3-Up...</span>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Select Print Fields */}
        {step === 2 && (
          <div className="flex flex-col gap-4 py-2">
            <div className="flex items-center justify-between flex-wrap gap-3 bg-blue-50 border border-blue-200 rounded-xl p-3.5 text-blue-950 text-xs md:text-sm">
              <div>
                <strong className="font-bold">Step 2: Select which fields should be printed as TEXT on the label</strong>
                <p className="mt-0.5 text-blue-900 text-xs">
                  Agar aap kisi field ka check hatate hain, to woh <strong>Label par text ke roop me print nahi hoga</strong>, lekin <strong>QR Code ke andar uska saara data HAMESHA encode rahega</strong>.
                </p>
              </div>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => setIsAddFieldOpen(true)}
                startIcon={<MdAdd />}
                className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs shadow-xs px-3.5 py-1.5 whitespace-nowrap"
              >
                Add Custom Field
              </Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {fields.map((f) => {
                const isSelected = f.printOnLabel !== false;
                return (
                  <div
                    key={f.id}
                    onClick={() => handleFieldChange(f.id, "printOnLabel", !isSelected)}
                    className={`p-3.5 rounded-xl border-2 cursor-pointer transition flex items-center justify-between relative group ${
                      isSelected
                        ? "border-emerald-600 bg-emerald-50/40 shadow-xs"
                        : "border-gray-200 bg-gray-50 opacity-60 hover:opacity-90"
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <Checkbox
                        checked={isSelected}
                        onChange={(e) => handleFieldChange(f.id, "printOnLabel", e.target.checked)}
                        color="success"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs">{getTypeIcon(f.type)}</span>
                          <span className="font-bold text-sm text-gray-900 block">
                            {f.label}
                          </span>
                          {f.isCustom && (
                            <span className="text-[10px] bg-purple-100 text-purple-800 font-bold px-1.5 py-0.2 rounded">
                              Custom
                            </span>
                          )}
                        </div>
                        <span className="text-[11px] text-gray-500">
                          {isSelected ? "✅ Will print on label" : "👁️ Hidden from label (In QR)"}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5">
                      <span
                        className={`text-xs px-2.5 py-1 rounded-full font-bold ${
                          isSelected
                            ? "bg-emerald-100 text-emerald-800"
                            : "bg-gray-200 text-gray-600"
                        }`}
                      >
                        {isSelected ? "PRINT" : "HIDE"}
                      </span>
                      {f.isCustom && (
                        <Tooltip title="Delete Field">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteField(f.id);
                            }}
                            className="text-red-500 hover:bg-red-50 p-1"
                          >
                            <MdDelete className="text-base" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center justify-between text-xs font-bold text-emerald-950 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <span className="text-base">📱</span>
                <span><strong>Total Fields:</strong> {fields.length} active in this template ({fields.filter(f => f.includeInQr !== false).length} in QR).</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="bg-emerald-700 text-white px-3 py-1 rounded-lg">
                  {activePrintFields.length} of {fields.length} Printed on Label
                </span>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setIsAddFieldOpen(true)}
                  startIcon={<MdAdd />}
                  className="text-xs font-bold text-emerald-800 border-emerald-500 hover:bg-emerald-100"
                >
                  + Add Field
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Field Settings & Admin Values */}
        {step === 3 && (
          <div className="flex flex-col gap-3 py-1">
            <div className="flex items-center justify-between flex-wrap gap-3 bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs md:text-sm text-amber-950">
              <div>
                <strong className="font-bold">Step 3: Field Settings, Custom Fields & Values</strong>
                <p className="mt-0.5 text-amber-900 text-xs">
                  Yahan Admin decide karega ki label par kya value aayegi, text ke aage prefix naam lagega ya nahi, aur customer khud edit kar sakta hai ya nahi.
                </p>
              </div>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => setIsAddFieldOpen(true)}
                startIcon={<MdAdd />}
                className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs shadow-xs px-3.5 py-1.5 whitespace-nowrap"
              >
                Add Custom Field
              </Button>
            </div>

            {/* Fields Table */}
            <div className="border border-gray-200 rounded-xl overflow-x-auto shadow-xs">
              <table className="w-full text-xs text-left">
                <thead className="bg-slate-100 font-bold text-gray-700 select-none">
                  <tr className="border-b border-gray-200">
                    <th className="py-2.5 px-3">Field Name</th>
                    <th className="py-2.5 px-2 text-center">Type</th>
                    <th className="py-2.5 px-2 text-center bg-blue-50/50">
                      <div className="font-bold text-blue-900">Print?</div>
                      <div className="text-[10px] text-blue-700 font-normal">On Label</div>
                    </th>
                    <th className="py-2.5 px-2 text-center bg-purple-50/50">
                      <div className="font-bold text-purple-900 flex items-center justify-center gap-0.5">
                        <span>📱</span> In QR?
                      </div>
                      <div className="text-[10px] text-purple-700 font-normal">Include in QR</div>
                    </th>
                    <th className="py-2.5 px-2 text-center bg-emerald-50/50">
                      <div className="font-bold text-emerald-900">Prefix?</div>
                      <div className="text-[10px] text-emerald-700 font-normal">"Name: abc"</div>
                    </th>
                    <th className="py-2.5 px-2 text-center bg-indigo-50/50">
                      <div className="font-bold text-indigo-900">To Customer?</div>
                      <div className="text-[10px] text-indigo-700 font-normal">Portal edit</div>
                    </th>
                    <th className="py-2.5 px-3 text-left bg-amber-50/50 min-w-[240px]">
                      <div className="font-bold text-amber-950 flex items-center gap-1">
                        <span>✍️</span> Admin Fill Value (Goes into QR & Label)
                      </div>
                    </th>
                    <th className="py-2.5 px-2 text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {fields.map((f) => {
                    const isPrintActive = f.printOnLabel !== false;
                    const listId = SUGGESTIONS[f.id] ? `${f.id}-options` : undefined;

                    return (
                      <tr
                        key={f.id}
                        className={`transition ${isPrintActive ? "hover:bg-slate-50" : "bg-gray-50/70"}`}
                      >
                        {/* Field Name */}
                        <td className="py-2.5 px-3 font-bold text-gray-800">
                          <div className="flex items-center gap-1.5">
                            <span
                              className={`w-2 h-2 rounded-full ${
                                isPrintActive ? "bg-emerald-600" : "bg-gray-400"
                              }`}
                            ></span>
                            <span>{f.label}</span>
                            {f.isCustom && (
                              <span className="text-[9px] bg-purple-100 text-purple-800 font-bold px-1 rounded">
                                Custom
                              </span>
                            )}
                          </div>
                          {!isPrintActive && (
                            <span className="text-[10px] text-amber-600 font-medium ml-3.5 block">
                              (Hidden from label{f.includeInQr !== false ? "; in QR" : ""})
                            </span>
                          )}
                        </td>

                        {/* Type Badge */}
                        <td className="py-2 px-2 text-center">
                          <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-700 border border-slate-200">
                            <span>{getTypeIcon(f.type)}</span>
                            <span className="capitalize">{f.type || "text"}</span>
                          </span>
                        </td>

                        {/* Print on Label Checkbox */}
                        <td className="py-2 px-2 text-center bg-blue-50/20">
                          <Checkbox
                            checked={isPrintActive}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setFields((prev) =>
                                prev.map((item) =>
                                  item.id === f.id
                                    ? {
                                        ...item,
                                        printOnLabel: checked,
                                        includeInQr: checked && item.type !== "image" ? true : (item.includeInQr !== undefined ? item.includeInQr : true),
                                      }
                                    : item
                                )
                              );
                            }}
                            color="primary"
                            size="small"
                          />
                        </td>

                        {/* In QR Code Checkbox */}
                        <td className="py-2 px-2 text-center bg-purple-50/20 border-x border-purple-100">
                          <Checkbox
                            checked={f.includeInQr !== false}
                            onChange={(e) =>
                              handleFieldChange(f.id, "includeInQr", e.target.checked)
                            }
                            color="secondary"
                            size="small"
                          />
                        </td>

                        {/* Print Prefix Checkbox */}
                        <td className="py-2 px-2 text-center bg-emerald-50/20">
                          {f.type === "image" ? (
                            <span className="text-[10px] text-gray-400">—</span>
                          ) : (
                            <Checkbox
                              checked={f.printPrefix}
                              onChange={(e) =>
                                handleFieldChange(f.id, "printPrefix", e.target.checked)
                              }
                              color="success"
                              size="small"
                            />
                          )}
                        </td>

                        {/* Show to Customer Checkbox */}
                        <td className="py-2 px-2 text-center bg-indigo-50/20">
                          <Checkbox
                            checked={f.showToCustomer}
                            onChange={(e) =>
                              handleFieldChange(f.id, "showToCustomer", e.target.checked)
                            }
                            color="primary"
                            size="small"
                          />
                        </td>

                        {/* Admin Fill Value */}
                        <td className="py-2 px-3 bg-amber-50/10">
                          {f.type === "image" ? (
                            <div className="flex items-center gap-2">
                              {f.defaultValue ? (
                                <img
                                  src={f.defaultValue}
                                  alt={f.label}
                                  className="w-8 h-8 object-contain rounded border border-gray-300 bg-white p-0.5"
                                />
                              ) : (
                                <div className="w-8 h-8 rounded border border-dashed border-gray-300 flex items-center justify-center text-[9px] text-gray-400 bg-white">
                                  🖼️
                                </div>
                              )}
                              <label className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-700 rounded text-[11px] font-bold cursor-pointer transition">
                                <MdCloudUpload className="text-sm" />
                                <span>{f.defaultValue ? "Change" : "Upload"}</span>
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) handleImageUpload(file, (val) => handleFieldChange(f.id, "defaultValue", val));
                                  }}
                                />
                              </label>
                              <input
                                type="text"
                                placeholder="Or Image URL"
                                className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs outline-none bg-white"
                                value={f.defaultValue && f.defaultValue.startsWith("data:") ? "" : f.defaultValue || ""}
                                onChange={(e) => handleFieldChange(f.id, "defaultValue", e.target.value)}
                              />
                            </div>
                          ) : (
                            <div className="relative">
                              <input
                                type={f.type === "number" ? "number" : "text"}
                                list={listId}
                                className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-xs bg-white text-gray-900 focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 outline-none shadow-xs font-medium"
                                placeholder={
                                  f.id === "srNo"
                                    ? "Auto sequence (1 to N)"
                                    : `Type or select ${f.label}`
                                }
                                value={f.defaultValue || ""}
                                onChange={(e) =>
                                  handleFieldChange(f.id, "defaultValue", e.target.value)
                                }
                              />
                              {SUGGESTIONS[f.id] && (
                                <span className="text-[10px] text-gray-400 absolute right-2 top-2 pointer-events-none">
                                  ▾
                                </span>
                              )}
                            </div>
                          )}

                          {f.id === "srNo" && (
                            <div className="mt-1 flex items-center gap-1 text-[10px] text-emerald-800 font-mono bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                              <span className="font-bold">Seq:</span>
                              <span>{computeSequentialSerial(f.defaultValue || "1", 0)}, {computeSequentialSerial(f.defaultValue || "1", 1)}, {computeSequentialSerial(f.defaultValue || "1", 2)}...</span>
                            </div>
                          )}
                        </td>

                        {/* Actions (Delete for custom fields) */}
                        <td className="py-2 px-2 text-center">
                          {f.isCustom ? (
                            <Tooltip title="Delete this custom field">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteField(f.id)}
                                className="text-red-500 hover:bg-red-50 p-1"
                              >
                                <MdDelete className="text-base" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <span className="text-[10px] text-gray-400 select-none">Fixed</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Step 4: FULL FREEFORM 2D VISUAL LABEL STUDIO (Drag & Edit) */}
        {step === 4 && (
          <div className="flex flex-col gap-3 py-1">
            {/* Studio Action & Presets Bar */}
            <div className="bg-slate-900 text-white p-3 rounded-2xl shadow-md flex flex-wrap items-center justify-between gap-3 text-xs">
              <div className="flex items-center gap-2">
                <span className="font-black text-emerald-400 flex items-center gap-1 text-sm">
                  <span>🎨</span> QR Print Text Studio & Builder
                </span>
                <span className="text-slate-400 text-[11px] hidden sm:inline">| Layout Presets:</span>
                <button
                  type="button"
                  onClick={() => applyPreset("qr-right")}
                  className="bg-slate-800 hover:bg-slate-700 px-2.5 py-1 rounded-lg text-emerald-300 font-bold transition cursor-pointer"
                >
                  QR Right
                </button>
                <button
                  type="button"
                  onClick={() => applyPreset("qr-left")}
                  className="bg-slate-800 hover:bg-slate-700 px-2.5 py-1 rounded-lg text-emerald-300 font-bold transition cursor-pointer"
                >
                  QR Left
                </button>
                <button
                  type="button"
                  onClick={() => applyPreset("two-column")}
                  className="bg-slate-800 hover:bg-slate-700 px-2.5 py-1 rounded-lg text-emerald-300 font-bold transition cursor-pointer"
                >
                  2-Column
                </button>
              </div>

              {/* Border Styles */}
              <div className="flex items-center gap-1.5 bg-slate-800 px-2 py-1 rounded-xl">
                <span className="text-slate-400 text-[11px]">Border:</span>
                {[
                  { id: "standard", label: "Thick" },
                  { id: "thin", label: "Thin" },
                  { id: "dotted", label: "Dotted" },
                  { id: "none", label: "None" },
                ].map((b) => (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => setBorderStyle(b.id)}
                    className={`px-2 py-1 rounded-lg font-bold transition ${
                      borderStyle === b.id ? "bg-indigo-600 text-white shadow-xs" : "text-slate-300 hover:text-white"
                    }`}
                  >
                    {b.label}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setIsAddFieldOpen(true)}
                  startIcon={<MdAdd />}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs px-3 py-1"
                >
                  + Add Field
                </Button>
              </div>
            </div>

            {/* SELECTED ELEMENT INSPECTOR TOOLBAR */}
            <div className="bg-indigo-950/90 text-white px-4 py-2 rounded-xl shadow-inner flex flex-wrap items-center justify-between gap-3 text-xs border border-indigo-800/80">
              <div className="flex items-center gap-2">
                <span className="text-indigo-300">Active Item:</span>
                <span className="bg-indigo-600 text-white px-2.5 py-0.5 rounded-md font-bold tracking-wide flex items-center gap-1">
                  {selectedId === "qrCode" ? (
                    <>
                      <MdQrCode2 className="text-base" /> QR Code
                    </>
                  ) : (
                    <>
                      <span>{getTypeIcon(selectedField?.type)}</span>
                      <span>QR Print Text — {selectedField?.label || selectedId}</span>
                    </>
                  )}
                </span>
              </div>

              {/* Controls for QR Code */}
              {selectedId === "qrCode" && (
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-1.5 bg-indigo-900/80 px-2 py-1 rounded-lg border border-indigo-700">
                    <span className="text-indigo-200 font-bold">📏 QR Size:</span>
                    <button
                      type="button"
                      onClick={() => setQrDimension(60)}
                      className={`px-2 py-0.5 rounded font-bold cursor-pointer ${qrDimension === 60 ? "bg-emerald-600 text-white shadow-xs" : "text-indigo-200 hover:text-white hover:bg-indigo-800/60"}`}
                    >
                      Small (60px)
                    </button>
                    <button
                      type="button"
                      onClick={() => setQrDimension(82)}
                      className={`px-2 py-0.5 rounded font-bold cursor-pointer ${qrDimension === 82 ? "bg-emerald-600 text-white shadow-xs" : "text-indigo-200 hover:text-white hover:bg-indigo-800/60"}`}
                    >
                      Normal (82px)
                    </button>
                    <button
                      type="button"
                      onClick={() => setQrDimension(105)}
                      className={`px-2 py-0.5 rounded font-bold cursor-pointer ${qrDimension === 105 ? "bg-emerald-600 text-white shadow-xs" : "text-indigo-200 hover:text-white hover:bg-indigo-800/60"}`}
                    >
                      Large (105px)
                    </button>

                    {/* Customizable QR Size Input */}
                    <div className="flex items-center gap-1 pl-1.5 ml-1 border-l border-indigo-600">
                      <span className="text-[11px] text-indigo-300 font-bold">Custom:</span>
                      <input
                        type="number"
                        min={20}
                        max={300}
                        value={qrDimension}
                        onChange={(e) => {
                          const val = parseInt(e.target.value, 10);
                          if (!isNaN(val)) {
                            setQrDimension(Math.max(20, Math.min(300, val)));
                          }
                        }}
                        className="w-14 bg-slate-900 text-white font-mono font-bold text-xs px-1.5 py-0.5 rounded border border-indigo-500 focus:outline-none focus:ring-1 focus:ring-emerald-400 text-center"
                      />
                      <span className="text-[10px] text-indigo-300 font-bold">px</span>
                      <div className="flex flex-col gap-0.5">
                        <button
                          type="button"
                          onClick={() => setQrDimension((prev) => Math.min(300, prev + 5))}
                          className="px-1 py-0 bg-indigo-800 hover:bg-indigo-700 text-white text-[9px] rounded leading-none font-bold cursor-pointer"
                          title="Increase QR size by 5px"
                        >
                          ▲
                        </button>
                        <button
                          type="button"
                          onClick={() => setQrDimension((prev) => Math.max(20, prev - 5))}
                          className="px-1 py-0 bg-indigo-800 hover:bg-indigo-700 text-white text-[9px] rounded leading-none font-bold cursor-pointer"
                          title="Decrease QR size by 5px"
                        >
                          ▼
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Print QR Toggle Button in Inspector */}
                  <button
                    type="button"
                    onClick={() => setPrintQr(!printQr)}
                    className={`px-3 py-1 rounded-lg font-bold text-xs transition flex items-center gap-1.5 border cursor-pointer ${
                      printQr
                        ? "bg-emerald-600 hover:bg-emerald-500 text-white border-emerald-400 shadow-xs"
                        : "bg-rose-700 hover:bg-rose-600 text-white border-rose-500 shadow-xs"
                    }`}
                    title="Click to toggle whether QR code is printed on physical labels"
                  >
                    <span>{printQr ? "🖨️ QR Print: ON" : "🚫 QR Print: DISABLED"}</span>
                  </button>

                  {/* Nudge buttons */}
                  <div className="flex items-center gap-1 bg-indigo-900/80 px-2 py-1 rounded-lg border border-indigo-700">
                    <span className="text-indigo-200 mr-1">Position:</span>
                    <button type="button" onClick={() => nudgeSelected(-1, 0)} className="px-1.5 py-0.5 bg-indigo-800 hover:bg-indigo-700 rounded font-bold cursor-pointer">◀</button>
                    <button type="button" onClick={() => nudgeSelected(1, 0)} className="px-1.5 py-0.5 bg-indigo-800 hover:bg-indigo-700 rounded font-bold cursor-pointer">▶</button>
                    <button type="button" onClick={() => nudgeSelected(0, -1)} className="px-1.5 py-0.5 bg-indigo-800 hover:bg-indigo-700 rounded font-bold cursor-pointer">▲</button>
                    <button type="button" onClick={() => nudgeSelected(0, 1)} className="px-1.5 py-0.5 bg-indigo-800 hover:bg-indigo-700 rounded font-bold cursor-pointer">▼</button>
                    <span className="text-[11px] text-indigo-300 ml-1 font-mono">({qrX}%, {qrY}%)</span>
                  </div>
                </div>
              )}

              {/* Controls for Image Field */}
              {selectedId !== "qrCode" && selectedField && selectedField.type === "image" && (
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-1.5 bg-indigo-900/80 px-2 py-1 rounded-lg border border-indigo-700">
                    <span className="text-indigo-200">Width:</span>
                    <button
                      type="button"
                      onClick={() => handleFieldChange(selectedField.id, "imageWidth", Math.max(15, (selectedField.imageWidth || 40) - 5))}
                      className="px-1.5 py-0.5 bg-indigo-800 hover:bg-indigo-700 rounded font-bold text-xs"
                    >
                      -
                    </button>
                    <span className="font-mono text-emerald-300 font-bold px-1">{selectedField.imageWidth || 40}px</span>
                    <button
                      type="button"
                      onClick={() => handleFieldChange(selectedField.id, "imageWidth", Math.min(200, (selectedField.imageWidth || 40) + 5))}
                      className="px-1.5 py-0.5 bg-indigo-800 hover:bg-indigo-700 rounded font-bold text-xs"
                    >
                      +
                    </button>
                  </div>

                  <div className="flex items-center gap-1.5 bg-indigo-900/80 px-2 py-1 rounded-lg border border-indigo-700">
                    <span className="text-indigo-200">Height:</span>
                    <button
                      type="button"
                      onClick={() => handleFieldChange(selectedField.id, "imageHeight", Math.max(15, (selectedField.imageHeight || 40) - 5))}
                      className="px-1.5 py-0.5 bg-indigo-800 hover:bg-indigo-700 rounded font-bold text-xs"
                    >
                      -
                    </button>
                    <span className="font-mono text-emerald-300 font-bold px-1">{selectedField.imageHeight || 40}px</span>
                    <button
                      type="button"
                      onClick={() => handleFieldChange(selectedField.id, "imageHeight", Math.min(200, (selectedField.imageHeight || 40) + 5))}
                      className="px-1.5 py-0.5 bg-indigo-800 hover:bg-indigo-700 rounded font-bold text-xs"
                    >
                      +
                    </button>
                  </div>

                  <label className="inline-flex items-center gap-1 bg-indigo-800 hover:bg-indigo-700 px-2 py-1 rounded-lg cursor-pointer text-indigo-200 hover:text-white border border-indigo-700 font-bold">
                    <MdCloudUpload className="text-sm" /> Replace Image
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleImageUpload(file, (val) => handleFieldChange(selectedField.id, "defaultValue", val));
                      }}
                    />
                  </label>

                  {/* Nudge Buttons */}
                  <div className="flex items-center gap-1 bg-indigo-900/80 px-2 py-1 rounded-lg border border-indigo-700">
                    <button type="button" onClick={() => nudgeSelected(-1, 0)} className="px-1.5 py-0.5 bg-indigo-800 hover:bg-indigo-700 rounded font-bold">◀</button>
                    <button type="button" onClick={() => nudgeSelected(1, 0)} className="px-1.5 py-0.5 bg-indigo-800 hover:bg-indigo-700 rounded font-bold">▶</button>
                    <button type="button" onClick={() => nudgeSelected(0, -1)} className="px-1.5 py-0.5 bg-indigo-800 hover:bg-indigo-700 rounded font-bold">▲</button>
                    <button type="button" onClick={() => nudgeSelected(0, 1)} className="px-1.5 py-0.5 bg-indigo-800 hover:bg-indigo-700 rounded font-bold">▼</button>
                    <span className="text-[11px] text-indigo-300 ml-1 font-mono">
                      ({selectedField.x || 0}%, {selectedField.y || 0}%)
                    </span>
                  </div>

                  {selectedField.isCustom && (
                    <button
                      type="button"
                      onClick={() => handleDeleteField(selectedField.id)}
                      className="px-2 py-1 bg-red-600/80 hover:bg-red-600 text-white rounded-lg font-bold flex items-center gap-1 border border-red-500"
                    >
                      <MdDelete className="text-sm" /> Delete
                    </button>
                  )}
                </div>
              )}

              {/* Controls for Text & Number Field */}
              {selectedId !== "qrCode" && selectedField && selectedField.type !== "image" && (
                <div className="flex items-center gap-3 flex-wrap">
                  {/* Font Size */}
                  <div className="flex items-center gap-1.5 bg-indigo-900/80 px-2 py-1 rounded-lg border border-indigo-700">
                    <span className="text-indigo-200">Font:</span>
                    <button
                      type="button"
                      onClick={() =>
                        handleFieldChange(
                          selectedField.id,
                          "fontSize",
                          Math.max(8, (selectedField.fontSize || 11) - 1)
                        )
                      }
                      className="px-1.5 py-0.5 bg-indigo-800 hover:bg-indigo-700 rounded font-bold text-xs"
                    >
                      A-
                    </button>
                    <span className="font-mono text-emerald-300 font-bold px-1">
                      {selectedField.fontSize || 11}px
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        handleFieldChange(
                          selectedField.id,
                          "fontSize",
                          Math.min(22, (selectedField.fontSize || 11) + 1)
                        )
                      }
                      className="px-1.5 py-0.5 bg-indigo-800 hover:bg-indigo-700 rounded font-bold text-xs"
                    >
                      A+
                    </button>
                  </div>

                  {/* Bold Toggle */}
                  <button
                    type="button"
                    onClick={() =>
                      handleFieldChange(
                        selectedField.id,
                        "fontWeight",
                        selectedField.fontWeight === "bold" ? "normal" : "bold"
                      )
                    }
                    className={`px-2.5 py-1 rounded-lg font-bold border transition ${
                      selectedField.fontWeight === "bold"
                        ? "bg-emerald-600 border-emerald-500 text-white"
                        : "bg-indigo-900/80 border-indigo-700 text-indigo-200 hover:text-white"
                    }`}
                  >
                    <MdFormatBold className="text-base inline" /> Bold
                  </button>

                  {/* Prefix Toggle */}
                  <button
                    type="button"
                    onClick={() =>
                      handleFieldChange(
                        selectedField.id,
                        "printPrefix",
                        !selectedField.printPrefix
                      )
                    }
                    className={`px-2.5 py-1 rounded-lg font-bold border transition ${
                      selectedField.printPrefix
                        ? "bg-emerald-600 border-emerald-500 text-white"
                        : "bg-indigo-900/80 border-indigo-700 text-indigo-200 hover:text-white"
                    }`}
                  >
                    Prefix "{selectedField.label}:"
                  </button>

                  {/* Nudge Buttons */}
                  <div className="flex items-center gap-1 bg-indigo-900/80 px-2 py-1 rounded-lg border border-indigo-700">
                    <button type="button" onClick={() => nudgeSelected(-1, 0)} className="px-1.5 py-0.5 bg-indigo-800 hover:bg-indigo-700 rounded font-bold">◀</button>
                    <button type="button" onClick={() => nudgeSelected(1, 0)} className="px-1.5 py-0.5 bg-indigo-800 hover:bg-indigo-700 rounded font-bold">▶</button>
                    <button type="button" onClick={() => nudgeSelected(0, -1)} className="px-1.5 py-0.5 bg-indigo-800 hover:bg-indigo-700 rounded font-bold">▲</button>
                    <button type="button" onClick={() => nudgeSelected(0, 1)} className="px-1.5 py-0.5 bg-indigo-800 hover:bg-indigo-700 rounded font-bold">▼</button>
                    <span className="text-[11px] text-indigo-300 ml-1 font-mono">
                      ({selectedField.x || 0}%, {selectedField.y || 0}%)
                    </span>
                  </div>

                  {selectedField.isCustom && (
                    <button
                      type="button"
                      onClick={() => handleDeleteField(selectedField.id)}
                      className="px-2 py-1 bg-red-600/80 hover:bg-red-600 text-white rounded-lg font-bold flex items-center gap-1 border border-red-500"
                    >
                      <MdDelete className="text-sm" /> Delete
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* MASTER 2D FREEFORM DRAGGABLE CANVAS WORKSPACE */}
            <div className="bg-slate-300/70 p-5 rounded-2xl border border-slate-300 flex flex-col items-center gap-3 select-none">
              <div className="text-xs font-bold text-slate-700 flex items-center justify-between w-full max-w-xl">
                <span>🎯 Interactive Freeform Studio (Click & Drag Any Item Anywhere)</span>
                <span className="bg-white px-2.5 py-1 rounded-md border border-slate-300 text-slate-800">
                  Label Size: <strong>{length} mm × {height} mm</strong>
                </span>
              </div>

              {/* Physical Label Canvas */}
              <div
                ref={canvasRef}
                className={`bg-white shadow-2xl relative overflow-hidden transition-all ${getComputedBorderStyle(
                  borderStyle
                )}`}
                style={{
                  width: `${canvasWidthPx}px`,
                  height: `${canvasHeightPx}px`,
                  boxSizing: "border-box",
                  cursor: isDragging ? "grabbing" : "default",
                }}
              >
                {/* Subtle alignment grid pattern */}
                <div
                  className="absolute inset-0 pointer-events-none opacity-20"
                  style={{
                    backgroundImage:
                      "linear-gradient(to right, #cbd5e1 1px, transparent 1px), linear-gradient(to bottom, #cbd5e1 1px, transparent 1px)",
                    backgroundSize: "20px 20px",
                  }}
                />

                {/* 1. DRAGGABLE QR CODE ELEMENT */}
                <div
                  onMouseDown={(e) => handlePointerDown(e, "qrCode")}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedId("qrCode");
                  }}
                  className={`absolute p-1 bg-white cursor-grab active:cursor-grabbing select-none transition-shadow ${
                    selectedId === "qrCode"
                      ? "ring-2 ring-emerald-500 shadow-lg z-30"
                      : "hover:ring-1 hover:ring-slate-400 z-10"
                  }`}
                  style={{
                    left: `${qrX}%`,
                    top: `${qrY}%`,
                    border: "none",
                    boxSizing: "border-box",
                  }}
                  title="Drag QR Code anywhere on the label"
                >
                  <QRCodeSVG
                    value={getPreviewQrData("1")}
                    size={qrDimension}
                    level="M"
                  />
                  {!printQr && (
                    <div className="absolute inset-0 bg-rose-950/75 rounded flex flex-col items-center justify-center text-center p-1 pointer-events-none">
                      <span className="text-[10px] font-black text-rose-300 uppercase leading-tight">🚫 QR Disabled</span>
                      <span className="text-[8px] text-rose-200">Will not print</span>
                    </div>
                  )}
                  <div className="text-[9px] text-center text-gray-500 font-sans mt-0.5 font-bold flex items-center justify-center gap-0.5">
                    <MdDragIndicator className="text-xs" /> QR Code
                  </div>
                </div>

                {/* 2. DRAGGABLE FIELD ELEMENTS (Only rendered if printOnLabel !== false) */}
                {fields.map((f) => {
                  if (f.printOnLabel === false) return null;
                  const isSelected = selectedId === f.id;

                  if (f.type === "image") {
                    return (
                      <div
                        key={f.id}
                        onMouseDown={(e) => handlePointerDown(e, f.id)}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedId(f.id);
                        }}
                        className={`absolute rounded cursor-grab active:cursor-grabbing select-none transition-all p-0.5 ${
                          isSelected
                            ? "ring-2 ring-indigo-500 shadow-md z-30 bg-indigo-50/50"
                            : "hover:ring-1 hover:ring-slate-300 z-20"
                        }`}
                        style={{
                          left: `${f.x !== undefined ? f.x : 4}%`,
                          top: `${f.y !== undefined ? f.y : 8}%`,
                          width: `${f.imageWidth || 40}px`,
                          height: `${f.imageHeight || 40}px`,
                        }}
                        title={`Drag ${f.label} anywhere`}
                      >
                        {f.defaultValue ? (
                          <img
                            src={f.defaultValue}
                            alt={f.label}
                            className="w-full h-full object-contain pointer-events-none"
                          />
                        ) : (
                          <div className="w-full h-full bg-slate-100 border border-dashed border-slate-400 rounded flex flex-col items-center justify-center text-slate-400 text-[8px] pointer-events-none">
                            <span>🖼️</span>
                            <span className="truncate">{f.label}</span>
                          </div>
                        )}
                      </div>
                    );
                  }

                  const val =
                    f.defaultValue || (f.id === "srNo" ? "1" : `Sample ${f.label}`);
                  const displayText = f.printPrefix ? `${f.label}: ${val}` : val;
                  const fontSizePx = f.fontSize || 11;
                  const fontWeightStyle = f.fontWeight === "bold" ? "font-bold" : "font-normal";

                  return (
                    <div
                      key={f.id}
                      onMouseDown={(e) => handlePointerDown(e, f.id)}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedId(f.id);
                      }}
                      className={`absolute px-1.5 py-0.5 rounded cursor-grab active:cursor-grabbing font-mono text-black select-none whitespace-nowrap flex items-center gap-1 ${fontWeightStyle} transition-all ${
                        isSelected
                          ? "bg-indigo-50/90 ring-2 ring-indigo-500 shadow-md z-30 text-indigo-950"
                          : "hover:bg-slate-100/80 hover:ring-1 hover:ring-slate-300 z-20"
                      }`}
                      style={{
                        left: `${f.x !== undefined ? f.x : 4}%`,
                        top: `${f.y !== undefined ? f.y : 8}%`,
                        fontSize: `${fontSizePx}px`,
                        lineHeight: 1.2,
                      }}
                      title={`Drag ${f.label} anywhere`}
                    >
                      <span className="opacity-30 hover:opacity-100 text-slate-500">
                        <MdDragIndicator className="text-xs" />
                      </span>
                      <span>{displayText}</span>
                    </div>
                  );
                })}
              </div>

              {/* Step 4 Footer Banner */}
              <div className="flex items-center justify-between w-full max-w-xl text-xs text-slate-600 pt-1">
                <span>💡 Layout ko adjust karne ke baad Step 5 me <strong>Final Print Preview</strong> dekhein.</span>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={() => setStep(5)}
                  endIcon={<MdArrowForward />}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-xs shadow-xs px-3.5 py-1"
                >
                  Go to Step 5: Final Preview
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Step 5: DEDICATED FULL PRINT & SHEET PREVIEW */}
        {step === 5 && (
          <div className="flex flex-col gap-4 py-1">
            {/* Step 5 Banner */}
            <div className="bg-gradient-to-r from-emerald-900 to-teal-950 text-white p-4 rounded-2xl shadow-sm flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="inline-flex items-center gap-1.5 bg-emerald-700/60 px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider text-emerald-200 mb-1">
                  <MdVisibility className="text-sm" /> Step 5: Final Output Inspection
                </div>
                <h3 className="text-base md:text-lg font-black text-white">
                  Final Label & Multi-Up Sheet Print Preview
                </h3>
                <p className="text-xs text-emerald-200">
                  Yeh preview exact wahi hai jo Customer Portal (`/qr-portal`) par generate hoga aur printer se nikalega.
                </p>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  variant="outlined"
                  onClick={() => setStep(4)}
                  startIcon={<MdEdit />}
                  className="text-xs font-bold text-emerald-200 border-emerald-400/50 hover:bg-emerald-800/40"
                >
                  Edit in Studio
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleSave}
                  startIcon={<MdSave />}
                  disabled={saving}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs shadow-md px-5 py-2"
                >
                  {saving ? "Saving..." : "CONFIRM & SAVE FIXED LAYOUT"}
                </Button>
              </div>
            </div>

            {/* Layout Specs Badges */}
            <div className="flex items-center justify-between flex-wrap gap-2 text-xs bg-white p-3 rounded-xl border border-slate-200 text-slate-700">
              <div className="flex items-center gap-3 flex-wrap font-semibold">
                <span>📏 Size: <strong className="text-slate-900">{length} mm × {height} mm</strong></span>
                <span>•</span>
                <span>📑 Layout: <strong className="text-emerald-700 font-bold">{up} Up</strong> ({up} pcs per row)</span>
                <span>•</span>
                <span>📱 QR Placement: <strong className="text-slate-900">({qrX}%, {qrY}%)</strong></span>
                <span>•</span>
                <span>🏷️ Printed Fields: <strong className="text-slate-900">{activePrintFields.length} of {fields.length}</strong></span>
                <span>•</span>
                <span className="text-emerald-700 font-bold">📲 {fields.filter(f => f.includeInQr !== false).length} Fields In QR Code</span>
              </div>
              <span className="text-[11px] bg-slate-100 text-slate-600 px-2.5 py-1 rounded font-mono">
                Customer Status: <strong>Locked by Admin</strong>
              </span>
            </div>

            {/* MAIN PREVIEW AREA: MULTI-UP ROW SIMULATION */}
            <div className="bg-slate-200/90 p-5 rounded-2xl border border-slate-300 flex flex-col items-center gap-4 overflow-y-auto max-h-[500px]">
              {(() => {
                const srField = fields.find((f) => f.id === "srNo");
                const baseSr = srField?.defaultValue || "1";
                const firstSr = computeSequentialSerial(baseSr, 0);
                const lastSr = computeSequentialSerial(baseSr, up * 2 - 1);
                return (
                  <div className="text-xs font-bold text-slate-700 text-center bg-slate-100 px-3 py-1.5 rounded-lg border border-slate-300">
                    📄 Sheet / Row Layout Simulation (Sr. No: <span className="text-emerald-700">{firstSr}</span> to <span className="text-emerald-700">{lastSr}</span>)
                  </div>
                );
              })()}

              {/* Rows Grid */}
              <div className="flex flex-col gap-4 items-center w-full">
                {[0, 1].map((rowIndex) => (
                  <div
                    key={rowIndex}
                    className="grid gap-3 justify-center w-full"
                    style={{
                      gridTemplateColumns: `repeat(${up}, minmax(0, 1fr))`,
                      maxWidth: `${up * (previewSheetWidthPx + 16)}px`,
                    }}
                  >
                    {Array.from({ length: up }).map((_, colIndex) => {
                      const srField = fields.find((f) => f.id === "srNo");
                      const baseSr = srField?.defaultValue || "1";
                      const sampleSr = computeSequentialSerial(baseSr, rowIndex * up + colIndex);

                      return (
                        <div
                          key={colIndex}
                          className={`bg-white relative overflow-hidden shadow-md transition page-break-inside-avoid ${getComputedBorderStyle(
                            borderStyle
                          )}`}
                          style={{
                            width: `${previewSheetWidthPx}px`,
                            height: `${previewSheetHeightPx}px`,
                            boxSizing: "border-box",
                          }}
                        >
                          {/* Top Serial Number Watermark Badge */}
                          <div className="absolute top-1 right-1 pointer-events-none opacity-30 text-[9px] font-mono font-bold text-slate-400">
                            #{sampleSr}
                          </div>

                          {/* Fixed Absolute QR Code (rendered only if printQr !== false) */}
                          {printQr !== false && (
                            <div
                              className="absolute bg-white"
                              style={{
                                left: `${qrX}%`,
                                top: `${qrY}%`,
                              }}
                            >
                              <QRCodeSVG
                                value={getPreviewQrData(sampleSr)}
                                size={Math.round(qrDimension * previewScale)}
                                level="M"
                              />
                            </div>
                          )}

                          {/* Fixed Absolute Fields */}
                          {fields.map((f) => {
                            if (f.printOnLabel === false) return null;

                            if (f.type === "image") {
                              return (
                                <div
                                  key={f.id}
                                  className="absolute pointer-events-none"
                                  style={{
                                    left: `${f.x !== undefined ? f.x : 4}%`,
                                    top: `${f.y !== undefined ? f.y : 8}%`,
                                    width: `${Math.round((f.imageWidth || 40) * previewScale)}px`,
                                    height: `${Math.round((f.imageHeight || 40) * previewScale)}px`,
                                  }}
                                >
                                  {f.defaultValue ? (
                                    <img
                                      src={f.defaultValue}
                                      alt={f.label}
                                      className="w-full h-full object-contain"
                                    />
                                  ) : (
                                    <div className="w-full h-full bg-slate-100 border border-slate-300 rounded flex items-center justify-center text-[8px] text-slate-400">
                                      🖼️ {f.label}
                                    </div>
                                  )}
                                </div>
                              );
                            }

                            const val =
                              f.id === "srNo"
                                ? sampleSr
                                : f.defaultValue || `Sample ${f.label}`;
                            const text = f.printPrefix ? `${f.label}: ${val}` : val;

                            return (
                              <div
                                key={f.id}
                                className={`absolute font-mono text-black whitespace-nowrap ${
                                  f.fontWeight === "bold" ? "font-bold" : "font-normal"
                                }`}
                                style={{
                                  left: `${f.x !== undefined ? f.x : 4}%`,
                                  top: `${f.y !== undefined ? f.y : 8}%`,
                                  fontSize: `${Math.round((f.fontSize || 11) * previewScale)}px`,
                                  lineHeight: 1.2,
                                }}
                              >
                                {text}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </DialogContent>

      {/* FOOTER CONTROLS */}
      <DialogActions className="bg-slate-100 px-6 py-3 flex justify-between border-t border-gray-200">
        <div>
          {step > 1 && (
            <Button
              variant="outlined"
              onClick={() => setStep(step - 1)}
              startIcon={<MdArrowBack />}
              className="text-xs font-bold text-gray-700 border-gray-300 hover:bg-gray-200"
            >
              Back
            </Button>
          )}
        </div>

        <div className="flex gap-2.5">
          {step < 5 ? (
            <Button
              variant="contained"
              color="success"
              onClick={() => setStep(step + 1)}
              endIcon={<MdArrowForward />}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs shadow-sm px-5 py-2"
            >
              Next Step ({step === 4 ? "Preview" : "Next"})
            </Button>
          ) : (
            <Button
              variant="contained"
              color="success"
              onClick={handleSave}
              startIcon={<MdSave />}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs shadow-md px-6 py-2"
            >
              {saving ? "Saving..." : "Save Fixed Layout Template"}
            </Button>
          )}
        </div>
      </DialogActions>

      {/* ADD CUSTOM FIELD MODAL / DIALOG */}
      <Dialog open={isAddFieldOpen} onClose={() => setIsAddFieldOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle className="bg-slate-900 text-white flex items-center justify-between p-4">
          <div className="flex items-center gap-2 text-sm md:text-base font-bold">
            <MdAdd className="text-xl text-emerald-400" />
            <span>Add Custom Field to Label</span>
          </div>
          <IconButton onClick={() => setIsAddFieldOpen(false)} size="small" className="text-white hover:bg-slate-800">
            <MdClose />
          </IconButton>
        </DialogTitle>
        <DialogContent className="p-5 flex flex-col gap-4 bg-white">
          {/* Field Name */}
          <div>
            <label className="text-xs font-bold text-gray-700 block mb-1">
              Field Name / Title *
            </label>
            <input
              type="text"
              placeholder="e.g. Batch No, Variety, Net Weight, Company Logo..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500 font-medium"
              value={newFieldForm.label}
              onChange={(e) => setNewFieldForm((prev) => ({ ...prev, label: e.target.value }))}
            />
          </div>

          {/* Field Type Selector */}
          <div>
            <label className="text-xs font-bold text-gray-700 block mb-1">
              Field Type
            </label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { type: "text", label: "Text", icon: "🔤", desc: "Regular text / string" },
                { type: "number", label: "Number", icon: "🔢", desc: "Numeric value" },
                { type: "image", label: "Image / Logo", icon: "🖼️", desc: "Logo or Graphic stamp" },
              ].map((item) => (
                <button
                  key={item.type}
                  type="button"
                  onClick={() =>
                    setNewFieldForm((prev) => ({
                      ...prev,
                      type: item.type,
                      includeInQr: item.type === "image" ? false : prev.includeInQr,
                      printPrefix: item.type === "image" ? false : prev.printPrefix,
                    }))
                  }
                  className={`p-2.5 rounded-xl border text-left transition flex flex-col items-start gap-1 cursor-pointer ${
                    newFieldForm.type === item.type
                      ? "border-emerald-600 bg-emerald-50/70 text-emerald-900 font-bold ring-2 ring-emerald-500/30"
                      : "border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <span className="text-lg">{item.icon}</span>
                  <span className="text-xs font-bold">{item.label}</span>
                  <span className="text-[10px] text-gray-500 font-normal leading-tight">{item.desc}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Default Value based on type */}
          <div>
            <label className="text-xs font-bold text-gray-700 block mb-1">
              Default Value
            </label>
            {newFieldForm.type === "image" ? (
              <div className="flex flex-col gap-2 p-3 bg-slate-50 border border-slate-200 rounded-xl">
                <div className="flex items-center gap-3">
                  {newFieldForm.defaultValue ? (
                    <img
                      src={newFieldForm.defaultValue}
                      alt="Preview"
                      className="w-14 h-14 object-contain rounded border border-slate-300 bg-white p-1"
                    />
                  ) : (
                    <div className="w-14 h-14 rounded border border-dashed border-slate-300 flex flex-col items-center justify-center text-[10px] text-slate-400 bg-white">
                      <span>🖼️</span>
                      <span>No Image</span>
                    </div>
                  )}
                  <div className="flex-1 flex flex-col gap-1.5">
                    <label className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold cursor-pointer transition shadow-xs w-fit">
                      <MdCloudUpload className="text-base" /> Upload Image
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file)
                            handleImageUpload(file, (val) =>
                              setNewFieldForm((prev) => ({ ...prev, defaultValue: val }))
                            );
                        }}
                      />
                    </label>
                    <span className="text-[10px] text-gray-500">Supported: PNG, JPG, SVG (Max 3MB)</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-gray-500 font-medium">Or Image URL:</span>
                  <input
                    type="text"
                    placeholder="https://..."
                    className="flex-1 border border-gray-300 rounded-lg px-2.5 py-1 text-xs outline-none bg-white"
                    value={
                      newFieldForm.defaultValue && newFieldForm.defaultValue.startsWith("data:")
                        ? ""
                        : newFieldForm.defaultValue
                    }
                    onChange={(e) =>
                      setNewFieldForm((prev) => ({ ...prev, defaultValue: e.target.value }))
                    }
                  />
                </div>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <div>
                    <span className="text-[10px] font-bold text-gray-600 block">Default Width (px):</span>
                    <input
                      type="number"
                      min="10"
                      max="300"
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs bg-white"
                      value={newFieldForm.imageWidth || 40}
                      onChange={(e) =>
                        setNewFieldForm((prev) => ({ ...prev, imageWidth: Number(e.target.value) }))
                      }
                    />
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-gray-600 block">Default Height (px):</span>
                    <input
                      type="number"
                      min="10"
                      max="300"
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs bg-white"
                      value={newFieldForm.imageHeight || 40}
                      onChange={(e) =>
                        setNewFieldForm((prev) => ({ ...prev, imageHeight: Number(e.target.value) }))
                      }
                    />
                  </div>
                </div>
              </div>
            ) : newFieldForm.type === "number" ? (
              <input
                type="number"
                placeholder="e.g. 50, 100, 2024"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500"
                value={newFieldForm.defaultValue}
                onChange={(e) =>
                  setNewFieldForm((prev) => ({ ...prev, defaultValue: e.target.value }))
                }
              />
            ) : (
              <input
                type="text"
                placeholder="Enter default text value"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500"
                value={newFieldForm.defaultValue}
                onChange={(e) =>
                  setNewFieldForm((prev) => ({ ...prev, defaultValue: e.target.value }))
                }
              />
            )}
          </div>

          {/* Options Checklist */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex flex-col gap-2">
            <div className="text-xs font-bold text-gray-700">Display & Encoding Settings:</div>
            <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-gray-800">
              <Checkbox
                size="small"
                color="success"
                checked={newFieldForm.printOnLabel}
                onChange={(e) =>
                  setNewFieldForm((prev) => ({ ...prev, printOnLabel: e.target.checked }))
                }
              />
              <span>Print on Label Canvas</span>
            </label>
            {newFieldForm.type !== "image" && (
              <>
                <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-gray-800">
                  <Checkbox
                    size="small"
                    color="secondary"
                    checked={newFieldForm.includeInQr}
                    onChange={(e) =>
                      setNewFieldForm((prev) => ({ ...prev, includeInQr: e.target.checked }))
                    }
                  />
                  <span>Include in QR Code payload</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-gray-800">
                  <Checkbox
                    size="small"
                    color="primary"
                    checked={newFieldForm.printPrefix}
                    onChange={(e) =>
                      setNewFieldForm((prev) => ({ ...prev, printPrefix: e.target.checked }))
                    }
                  />
                  <span>Print prefix title ("{newFieldForm.label || 'Field'}: Value")</span>
                </label>
              </>
            )}
            <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-gray-800">
              <Checkbox
                size="small"
                color="primary"
                checked={newFieldForm.showToCustomer}
                onChange={(e) =>
                  setNewFieldForm((prev) => ({ ...prev, showToCustomer: e.target.checked }))
                }
              />
              <span>Allow Customer to edit on print portal</span>
            </label>
          </div>
        </DialogContent>
        <DialogActions className="p-4 bg-gray-50 border-t border-gray-200 flex justify-between">
          <Button onClick={() => setIsAddFieldOpen(false)} color="inherit" className="font-bold text-xs">
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleAddNewField}
            startIcon={<MdAdd />}
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs px-5 py-2"
          >
            Add Field to Label
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default QrTemplateModal;
